import { Alert, App, Button, Input, Modal, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { pasteNominees } from '../api';
import { NomineeLineIssue, NomineePasteResult, errorText } from '../contract';
import { pastePreview, splitPasteWarnings } from '../lib/nomination-status';
import styles from '../styles.module.scss';

const { TextArea } = Input;

const PLACEHOLDER = [
  '7381234567890123456, HP Dance Team',
  '7381234567890123457, HP Dance Team',
  '7381234567890123458',
].join('\n');

interface Props {
  eventId: number;
  categoryId: number;
  categoryName: string;
  open: boolean;
  onClose: () => void;
  /** Đã dán xong một đợt — nơi gọi tải lại danh sách đề cử. */
  onDone: () => void | Promise<void>;
}

const IssueTable = ({ rows }: { rows: NomineeLineIssue[] }) => (
  <Table
    style={{ marginTop: 8 }}
    size="small"
    rowKey={(r) => `${r.line}-${r.reason}`}
    dataSource={rows}
    pagination={rows.length > 10 ? { pageSize: 10, size: 'small' } : false}
    columns={[
      { title: 'Dòng', dataIndex: 'line', width: 70 },
      { title: 'Lý do', dataIndex: 'reason' },
    ]}
  />
);

/** Modal "Dán danh sách đề cử": mỗi dòng `UID[, tên đơn vị]`, cùng tên đơn vị thì gộp thành một đơn vị. */
const NomineePasteModal = ({ eventId, categoryId, categoryName, open, onClose, onDone }: Props) => {
  const { message } = App.useApp();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<NomineePasteResult | null>(null);

  // Mở lại modal (hoặc đổi hạng mục): bắt đầu sạch, không giữ danh sách cũ.
  useEffect(() => {
    if (!open) return;
    setText('');
    setResult(null);
  }, [open, categoryId]);

  const preview = useMemo(() => pastePreview(text), [text]);
  // Cảnh báo "bỏ qua" (trùng UID, đã ở đơn vị khác) khác cảnh báo "vẫn nhận" (chưa gắn tài khoản).
  const warns = useMemo(() => splitPasteWarnings(result?.warnings ?? []), [result]);

  const run = async () => {
    setBusy(true);
    try {
      const res = await pasteNominees(eventId, categoryId, text);
      setResult(res);
      await onDone();
    } catch (e) {
      message.error(errorText(e, 'Không dán được danh sách đề cử.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Dán danh sách đề cử — ${categoryName}`}
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Đóng</Button>}
      width={720}
      destroyOnClose
    >
      <p className={styles.hint}>
        Mỗi dòng một người: <code>UID, tên đơn vị</code> (cách nhau bằng dấu phẩy, tab hoặc{' '}
        <code>|</code>). Các dòng <strong>cùng tên đơn vị</strong> gộp thành một Nhóm LIVE; dòng chỉ
        có UID là một đơn vị riêng lấy tên trên vé. Người chưa có vé mời phải được thêm ở tab Vé mời
        trước.
      </p>
      <TextArea
        rows={9}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={PLACEHOLDER}
        aria-label="Danh sách đề cử"
      />
      <div style={{ marginTop: 8 }}>
        <strong>
          Nhận diện {preview.valid}/{preview.total} dòng · {preview.unitCount} đơn vị
        </strong>
        {preview.groups.length > 0 && (
          <span className={styles.subtle}>
            {' '}
            ({preview.groups.map((g) => `${g.name}: ${g.count} người`).join('; ')})
          </span>
        )}
        {preview.invalid.length > 0 && (
          <span className={styles.unlinked}>
            {' '}
            · Dòng không có UID: {preview.invalid.slice(0, 12).join(', ')}
            {preview.invalid.length > 12 ? '…' : ''}
          </span>
        )}
      </div>
      <Button
        type="primary"
        style={{ marginTop: 12 }}
        loading={busy}
        disabled={preview.valid === 0}
        onClick={run}
      >
        Thêm vào hạng mục
      </Button>

      {result && (
        <div style={{ marginTop: 16 }}>
          <Alert
            showIcon
            type={result.errors.length ? 'warning' : 'success'}
            message={`Mới ${result.created} · Cập nhật ${result.updated}`}
            description={
              result.errors.length
                ? `${result.errors.length} dòng bị bỏ qua, xem bảng bên dưới.`
                : undefined
            }
          />
          {result.errors.length > 0 && <IssueTable rows={result.errors} />}
          {warns.skipped.length > 0 && (
            <>
              <Alert
                showIcon
                type="warning"
                style={{ marginTop: 8 }}
                message={`${warns.skipped.length} dòng bị bỏ qua (trùng UID hoặc đã ở đơn vị khác)`}
              />
              <IssueTable rows={warns.skipped} />
            </>
          )}
          {warns.accepted.length > 0 && (
            <>
              <Alert
                showIcon
                type="warning"
                style={{ marginTop: 8 }}
                message={`${warns.accepted.length} cảnh báo (các dòng này vẫn được nhận)`}
              />
              <IssueTable rows={warns.accepted} />
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default NomineePasteModal;
